// src/pages/Quotation/QuotationEntry.jsx
import React, { useEffect, useState } from "react";
import { Row, Col, Button, Grid, Space } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import CreateQuotationForm from "./components/CreateQuotationForm";
import QuotationTable from "./components/QuotationTable";
import { createQuotation, fetchQuotationVoucherNumber } from "./quotationThunk";
import {
  resetQuotation,
  setCustomer,
  resetPreviousDue,
  addItemRow,
  setFooterValues,
  emptyItemCart,
  setSaleOrderId,
  setQuotationTagFieldId,
  setServicePayCart,
  setTaxMode,
} from "./quotationSlice";
import { resetLookups } from "../../store/commonSlices/lookupsSlice";
import Swal from "sweetalert2";
import { selectAuthInfo } from "../Auth/authSelectors";
import {
  fetchPreviousDue,
  fetchSingleInstitutionDetails,
  fetchTagGroupFields,
  fetchTagsOnEdit,
} from "../../store/commonThunks/lookups";
import {
  setIsEditQuotationRecord,
  setSelectedQuotationRecord,
} from "../QuotationRecord/QuotationRecordSlice";
import { getAccessType } from "../../lib/functions";
import {
  fetchDiscountAccounts,
  fetchTaxAccounts,
  fetchTransportAccounts,
} from "../Sales/salesThunks";
import { useForm } from "antd/es/form/Form";
import { selectInstitutionData } from "../../store/commonSelectors/lookupsSelectors";

const QuotationEntry = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const [form] = useForm();
  const { itemCart, customer, footerValues, voucherNumber, servicePayCart } =
    useSelector((s) => s.quotation);
  const { isEditQuotationRecord, selectedQuotationRecord } = useSelector(
    (s) => s.quotationRecord
  ); //for updating
  const authInfo = useSelector(selectAuthInfo);
  const institution = useSelector(selectInstitutionData);

  let [access_type, access_type_set] = useState("");

  const [selectedDiscountAcc, selectedDiscountAccSet] = useState(null);
  const [selectedTaxAcc, setSelectedTaxAcc] = useState(null);
  const [selectedTransportAcc, setSelectedTransportAcc] = useState(null);

  //reset on component unmounting
  useEffect(() => {
    return () => {
      dispatch(resetQuotation());
    };
  }, [dispatch]);

  useEffect(() => {
    const access = getAccessType("quotation_entry");
    access_type_set(access);
  }, []);

  useEffect(() => {
    dispatch(fetchTagGroupFields(""));
    dispatch(fetchSingleInstitutionDetails())

    dispatch(fetchDiscountAccounts()).then((res) => {
      if (res?.payload) {
        const discountAccount = res.payload;
        selectedDiscountAccSet(discountAccount);
      }
    });

    dispatch(fetchTaxAccounts()).then((res) => {
      if (res?.payload) {
        const taxAccount = res.payload;
        setSelectedTaxAcc(taxAccount);
      }
    });
    dispatch(fetchTransportAccounts()).then((res) => {
      if (res?.payload) {
        const TransportAccount = res.payload;
        setSelectedTransportAcc(TransportAccount);
      }
    });
  }, [dispatch]);

  // for filling up data from record on updating
  useEffect(() => {
    if (isEditQuotationRecord && selectedQuotationRecord) {
      const record = Array.isArray(selectedQuotationRecord)
        ? selectedQuotationRecord[0]
        : selectedQuotationRecord;
      const taxModeSelected = selectedQuotationRecord[0]?.is_cal_type == "individual" ? "on_item" : "on_total"
      console.log(selectedQuotationRecord[0], "selectedQuotationRecord1233 is_cal_type");
      if (taxModeSelected) {
        dispatch(setTaxMode(taxModeSelected))
      }

      // // ✅ Reset first to clear previous form
      // dispatch(resetQuotation());

      // Clear only necessary parts
      dispatch(emptyItemCart());

      // ✅ Fill quotation number
      dispatch({
        type: "quotation/setVoucherNumber",
        payload: record.sale_order_no,
      });

      dispatch(setSaleOrderId(record.sale_o_id));

      // ✅ Fill customer info (including institution)
      dispatch(
        setCustomer({
          acc_id: record.acc_id,
          acc_name: record.acc_name,
          contact_no: record.contact_no,
          address: record.address,
          institution_name: record.institution_name,
        })
      );

      let serviceCart = [];

      if (Number(record.other_service_cost || 0) > 0) {
        serviceCart = [
          {
            to_acc_id: record.transport_acc_id || null,
            to_acc_name: record.transport_acc_name || "",
            tran_amount: String(record.other_service_cost || 0),
          },
        ];
      }

      dispatch(setServicePayCart(serviceCart));


      // ✅ Fill footer totals
      dispatch(
        setFooterValues({
          subtotal: record.sub_total,
          total_discount: record.total_discount,
          totalTax: record.total_tax,
          grandTotal: record.total_amount,
          addExpenses: serviceCart.length > 0,
        })
      );

      form.setFieldValue("search_customer", {
        sale_id: record.acc_id,
        display_text: record.acc_name,
        raw: {
          acc_id: record.acc_id,
          acc_name: record.acc_name,
          contact_no: record.contact_no,
          address: record.address,
          institution_name: record.institution_name,
        }
      });


      // ✅ Fill item rows
// ✅ In QuotationEntry.jsx — isEditQuotationRecord useEffect
if (record.details?.length) {
  const mappedRows = record.details.map((it) => ({
    key: `${it.item_id}-${Date.now()}-${Math.random()}`,
    item_id: it.item_id,
    product: it.item_name,
    product_display: it.display_text || it.item_name,
    item_description: it.item_description || "",
    showDescription: !!it.item_description.length,
    qty: Number(it.item_qty || 0),
    rate: Number(it.item_rate || 0),
    discount: Number(it.item_discount_per || 0),  // ✅ use discount_per not discount
    discountAmt: Number(it.item_discount || 0),
    discountMode: it.discount_mode || "percent",
    tax_per: Number(it.item_tax_per || 0),
    unit_symbol: it.unit_symbol || "",
    // ✅ same detection as salesOrderSlice and CreateInvoiceForm
    bonus: parseFloat(it.item_rate) === 0,
    // ✅ store rate variants so useEffect in QuotationTable can switch correctly
    sale_rate: it.sale_rate,
    moh_invoice_rate: it.moh_invoice_rate,
    wholesaler_rate: it.wholesaler_rate,
    retailer_rate: it.retailer_rate,
    distributor_rate: it.distributor_rate,
    corporate_rate: it.corporate_rate,
    isBlank: false,
  }));

  mappedRows.forEach((row) => {
    dispatch(addItemRow(row));
  });
}

      dispatch(fetchTagsOnEdit({
        tab_id: record.sale_o_id,
        type: "quotation_entry"
      }))
        .unwrap()
        .then((tags) => {
          const tagIds = tags.map(t => t.id);
          dispatch(setQuotationTagFieldId(tagIds));
        });

    }
  }, [isEditQuotationRecord, selectedQuotationRecord, dispatch]);

  const handleSave = () => {
    // 1️⃣ Validate customer
    if (!customer || !customer.acc_id) {
      Swal.fire("Please select a customer!", "", "warning");
      return;
    }

    // 2️⃣ Validate item cart
    const validItems = (itemCart || []).filter((item) => item.item_id);
    if (validItems.length === 0) {
      Swal.fire("Item cart is Empty! Add Items", "", "warning");
      return;
    }

    // 3️⃣ Validate quantities (>0)
    const allValidQty = validItems.every(
      (item) => Number(item.qty || item.item_qty || 0) > 0
    );
    if (!allValidQty) {
      Swal.fire("All items must have quantity greater than 0!", "", "warning");
      return;
    }

    // 4️⃣ Totals not zero
    if (Number(footerValues.grandTotal || 0) <= 0) {
      Swal.fire("Total Amount should not be 0!", "", "warning");
      return;
    }

    // start save
    Swal.fire({
      title: "Saving Quotation...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    dispatch(
      createQuotation({
        location,
        selectedDiscountAcc,
        selectedTaxAcc,
        selectedTransportAcc,
      })
    )
      .unwrap()
      .then((res) => {
        Swal.close();
        if (!res.error) {
          // reset form after success
          dispatch(setCustomer(null));

          Swal.fire({
            title: "Quotation Created! Do you want to view voucher?",
            icon: "success",
            showCancelButton: true,
            confirmButtonText: "Yes, view voucher",
            cancelButtonText: "No, back",
          }).then((result) => {
            handleResetAll();
            if (result.isConfirmed) {
              // server expected to return sale_o_id or similar
              const id = res.sale_o_id || res.sale_id || res.id;
              if (id) navigate(`/quotation/quick-quotation-voucher/${id}`);
            } else {
              dispatch(resetLookups());
              navigate("/quotation/quotation");
            }
          });
        } else {
          Swal.fire(res.message || "Something went wrong", "", "warning");
        }
      })
      .catch(() => {
        Swal.close();
        Swal.fire("Network ERROR", "", "error");
      });
  };

  const handleResetAll = () => {
    dispatch(resetQuotation());
    dispatch(setIsEditQuotationRecord(false)); // ✅ stop edit mode
    dispatch(setSelectedQuotationRecord(null)); // ✅ clear loaded record
  };

  return (
    <div style={{ padding: screens.xs ? 12 : 20, overflowX: "hidden", width: "100%" }}>
      {!screens.xs && (
        <Row>
          <Col span={24}>
            <Space style={{ float: "right", marginBottom: 16 }}>
              <Button
                onClick={() => {
                  navigate("/quotation/dashboard");
                  handleResetAll();
                }}
              >
                Back
              </Button>
              <Button
                onClick={() => {
                  dispatch(resetQuotation());
                  dispatch(resetLookups());
                }}
              >
                Reset
              </Button>
              <Button type="primary" onClick={handleSave}>
                Save
              </Button>
            </Space>
          </Col>
        </Row>
      )}

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <CreateQuotationForm authInfo={authInfo} form={form} institution={institution}/>
        </Col>
        <Col span={24}>
          <QuotationTable authInfo={authInfo} institution={institution}/>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Space style={{ float: "right", marginTop: 16 }}>
            <Button
              onClick={() => {
                handleResetAll();
                navigate("/quotation/dashboard");
              }}
            >
              Back
            </Button>
            <Button
              onClick={() => {
                handleResetAll();
                dispatch(resetQuotation());
              }}
            >
              Reset
            </Button>
            {access_type == "WRITE" && (
              <>
                <Button
                  type="primary"
                  data-testid="save-button"
                  onClick={handleSave}
                >
                  Save
                </Button>
              </>
            )}
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default QuotationEntry;
